(() => {
	'use strict';

	const STORAGE_KEY = 'grungeclub-sms-optin';

	/**
	 * Build E.164 from country prefix + national number.
	 * Default FR (+33): strips leading 0 from the national part.
	 */
	function toE164(prefixRaw, phoneRaw) {
		let prefix = String(prefixRaw || '').trim();
		let phone = String(phoneRaw || '').trim();

		prefix = prefix.replace(/[^\d+]/g, '');
		if (prefix && !prefix.startsWith('+')) {
			prefix = `+${prefix}`;
		}

		phone = phone.replace(/[^\d]/g, '');

		if (!prefix || !phone) {
			return '';
		}

		// France national numbers often start with 0
		if ((prefix === '+33' || prefix === '33') && phone.startsWith('0')) {
			phone = phone.slice(1);
		}

		const e164 = `${prefix}${phone}`;
		if (!/^\+[1-9]\d{7,14}$/.test(e164)) {
			return '';
		}
		return e164;
	}

	class AxSmsOptin extends HTMLElement {
		constructor() {
			super();
			this.init();
		}

		get isLoggedIn() {
			return this.dataset.loggedIn === 'true';
		}

		get storageKey() {
			const customerKey = (this.dataset.customerKey || '').trim().toLowerCase();
			return customerKey ? `${STORAGE_KEY}:${customerKey}` : '';
		}

		redirectToLogin() {
			const loginUrl = this.dataset.loginUrl;
			if (loginUrl) {
				window.location.href = loginUrl;
			}
		}

		init() {
			localStorage.removeItem(STORAGE_KEY);

			if (!this.isLoggedIn) {
				return this.bindForm();
			}

			if (this.storageKey && localStorage.getItem(this.storageKey) === 'true') {
				this.classList.add('is-already-opted-in');
				const alreadyEl = this.querySelector('[data-already-opted-in]');
				if (alreadyEl) {
					alreadyEl.hidden = false;
				}
			}

			this.bindForm();
		}

		bindForm() {
			const form = this.querySelector('form.sms-optin-form');
			if (form) {
				form.addEventListener('submit', (event) => this.handleSubmit(event, form));
			}
		}

		handleSubmit(event, form) {
			event.preventDefault();
			event.stopPropagation();

			if (!this.isLoggedIn) {
				this.redirectToLogin();
				return;
			}

			const companyId = (this.dataset.klaviyoCompanyId || '').trim();
			const listId = (this.dataset.klaviyoListId || '').trim();

			if (!companyId) {
				this.showError(form, 'Klaviyo is not configured.');
				return;
			}

			if (!form.checkValidity()) {
				form.reportValidity();
				return;
			}

			const emailInput = form.querySelector('input[name="email"]');
			const prefixInput = form.querySelector('input[name="country_prefix"]');
			const phoneInput = form.querySelector('input[name="phone"]');
			const submitButton = form.querySelector('[type="submit"]');

			const email = (
				this.dataset.customerEmail ||
				(emailInput ? emailInput.value : '') ||
				''
			).trim();
			const e164 = toE164(
				prefixInput ? prefixInput.value : this.dataset.defaultCountry,
				phoneInput ? phoneInput.value : ''
			);

			this.hideError(form);

			if (!email || !e164) {
				this.showError(form, null);
				if (phoneInput) {
					phoneInput.setCustomValidity(
						phoneInput.validationMessage || 'Invalid phone number'
					);
					phoneInput.reportValidity();
					phoneInput.setCustomValidity('');
				}
				return;
			}

			if (submitButton) {
				submitButton.disabled = true;
			}

			this.subscribeSms(companyId, listId, email, e164)
				.then(() => {
					if (this.storageKey) {
						localStorage.setItem(this.storageKey, 'true');
					}
					this.setAttribute('data-posted-successfully', 'true');
					this.classList.remove('is-already-opted-in');

					const successEl = form.querySelector('[data-klaviyo-success]');
					if (successEl) {
						successEl.hidden = false;
					}

					const alreadyEl = this.querySelector('[data-already-opted-in]');
					if (alreadyEl) {
						alreadyEl.hidden = true;
					}
				})
				.catch((error) => {
					console.error('[Ax SMS Opt-in] Klaviyo failed', error);
					this.showError(form, null);
				})
				.finally(() => {
					if (submitButton) {
						submitButton.disabled = false;
					}
				});
		}

		hideError(form) {
			const errorEl = form.querySelector('[data-klaviyo-error]');
			if (!errorEl) return;
			errorEl.hidden = true;
			errorEl.textContent = '';
		}

		showError(form, message) {
			const errorEl = form.querySelector('[data-klaviyo-error]');
			if (!errorEl) return;
			errorEl.textContent =
				message ||
				errorEl.dataset.errorMessage ||
				'An error occurred during subscription.';
			errorEl.hidden = false;
		}

		subscribeSms(companyId, listId, email, phoneE164) {
			const url = `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`;
			const consentedAt = new Date().toISOString();

			const profileAttributes = {
				email: email,
				phone_number: phoneE164,
				properties: {
					sms_optin: true,
					sms_optin_date: consentedAt,
					consented_at: consentedAt,
					sms_optin_source: 'GRUNGECLUB'
				},
				subscriptions: {
					sms: {
						marketing: {
							consent: 'SUBSCRIBED'
						}
					}
				}
			};

			const data = {
				type: 'subscription',
				attributes: {
					custom_source: 'GRUNGECLUB SMS opt-in',
					profile: {
						data: {
							type: 'profile',
							attributes: profileAttributes
						}
					}
				}
			};

			if (listId) {
				data.relationships = {
					list: {
						data: {
							type: 'list',
							id: listId
						}
					}
				};
			}

			return fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/vnd.api+json',
					Accept: 'application/vnd.api+json',
					revision: '2025-10-15'
				},
				body: JSON.stringify({ data })
			}).then(async (response) => {
				if (response.status === 202 || response.ok) {
					return;
				}

				let detail = '';
				try {
					const body = await response.json();
					detail = body?.errors?.[0]?.detail || JSON.stringify(body);
				} catch (_err) {
					// ignore
				}

				throw new Error(
					`Klaviyo SMS subscribe failed (${response.status})${detail ? `: ${detail}` : ''}`
				);
			});
		}
	}

	if (!customElements.get('ax-sms-optin')) {
		customElements.define('ax-sms-optin', AxSmsOptin);
	}
})();
